// React
import { useReducer, createContext, useContext, ReactElement, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
// Contexts
import { useTenancy } from '@utmb/contexts/TenancyContext';
// Reducers
import {
    ActionKind,
    AddNewQualifiersAction,
    FetchQualifiersAction,
    FilterOnCategoryAction,
    FilterOnCountryAction,
    FilterOnDateAction,
    initialState,
    ChangePageQualifiersAction,
    reducer,
    RestoreFiltersAction,
    SetQualifiersAction,
    SetSearchAction,
} from '@utmb/reducers/qualifiers';
// Interfaces
import { QualifiersContextProps, QualifiersProviderProps, SearchQualifierDto, StoredFilters } from '@utmb/types/qualifiers';
import SearchQualifiersRaceResultView from '@utmb/types/view/SearchQualifiersRaceResultView';
// Utils
import { fetchClientAPI } from '@utmb/utils/utmb';
import { buildQuery } from '@utmb/utils/query';
import { formatDateToApi } from '@utmb/utils/intl';

const QualifiersContext = createContext({} as QualifiersContextProps);

const fetchClientQualifiers = async (tenantId: string, searchQualifierDto: SearchQualifierDto): Promise<SearchQualifiersRaceResultView> => {
    const dto = {
        ...searchQualifierDto,
    } as any;

    if (searchQualifierDto.dateMin) {
        dto.dateMin = formatDateToApi(searchQualifierDto.dateMin);
    }

    if (searchQualifierDto.dateMax) {
        dto.dateMax = formatDateToApi(searchQualifierDto.dateMax);
    }

    const path = buildQuery(`/search/races-qualifiers`, dto);

    return await fetchClientAPI(tenantId, path);
};

export const QualifiersProvider = ({ result: { races, nbHits, limit }, filters, children }: QualifiersProviderProps | any): ReactElement => {
    const { tenantId } = useTenancy();
    const { locale } = useRouter();

    const localStorageFilterKey = 'utmb-qualifiers';

    const [state, dispatch] = useReducer(reducer, { ...initialState, races, nbHits, filters, limit });

    const { search, selectedCategories, selectedCountries, selectedDateMin, selectedDateMax } = state;

    const [isInit, setIsInit] = useState(false);

    useEffect(() => {
        fetchQualifiers().then();

        if (isInit) {
            saveFilter();
        }

        setIsInit(true);
    }, [search, selectedCategories, selectedCountries, selectedDateMin, selectedDateMax]);

    useEffect(() => {
        const storedFiltersItem = sessionStorage.getItem(localStorageFilterKey);

        if (storedFiltersItem) {
            const storedFilters = JSON.parse(storedFiltersItem) as StoredFilters;
            const filters = {
                ...storedFilters,
                selectedDateMin: new Date(storedFilters.selectedDateMin),
                selectedDateMax: new Date(storedFilters.selectedDateMax),
            };

            dispatch({ type: ActionKind.RESTORE_FILTERS, payload: filters } as RestoreFiltersAction);
        }
    }, []);

    const saveFilter = (): void => {
        sessionStorage.setItem(
            localStorageFilterKey,
            JSON.stringify({ search, selectedCategories, selectedCountries, selectedDateMin, selectedDateMax } as StoredFilters)
        );
    };

    const fetchQualifiers = async (): Promise<void> => {
        dispatch({ type: ActionKind.FETCH_QUALIFIERS } as FetchQualifiersAction);

        const { races, nbHits, limit } = await fetchClientQualifiers(tenantId, {
            lang: locale,
            search,
            categories: selectedCategories,
            countryCodes: selectedCountries,
            dateMin: selectedDateMin,
            dateMax: selectedDateMax,
            offset: 0,
            limit: state.limit,
        });

        dispatch({
            type: ActionKind.SET_QUALIFIERS,
            payload: {
                races,
                nbHits,
                limit,
            },
        } as SetQualifiersAction);
    };

    const changePageQualifiers = async (page: number): Promise<void> => {
        dispatch({ type: ActionKind.CHANGE_PAGE_QUALIFIERS } as ChangePageQualifiersAction);

        const { races, offset } = await fetchClientQualifiers(tenantId, {
            lang: locale,
            search,
            categories: selectedCategories,
            countryCodes: selectedCountries,
            dateMin: selectedDateMin,
            dateMax: selectedDateMax,
            offset: page * state.limit,
            limit,
        });

        dispatch({ type: ActionKind.ADD_NEW_QUALIFIERS, payload: { races, offset } } as AddNewQualifiersAction);
    };

    const handleCheckCategory = (value: string): void => {
        dispatch({ type: ActionKind.FILTER_ON_CATEGORY, payload: value } as FilterOnCategoryAction);
    };

    const handleCheckCountry = (value: string): void => {
        dispatch({ type: ActionKind.FILTER_ON_COUNTRY, payload: value } as FilterOnCountryAction);
    };

    const handleCheckDate = (dateMin?: Date, dateMax?: Date): void => {
        dispatch({ type: ActionKind.FILTER_ON_DATE, payload: { dateMin, dateMax } } as FilterOnDateAction);
    };

    const handleChangeSearch = (search: string): void => {
        dispatch({ type: ActionKind.SET_SEARCH, payload: search } as SetSearchAction);
    };

    return (
        <QualifiersContext.Provider
            value={{
                ...state,
                changePageQualifiers,
                handleCheckCategory,
                handleCheckCountry,
                handleCheckDate,
                handleChangeSearch,
            }}
        >
            {children}
        </QualifiersContext.Provider>
    );
};

export const useQualifiers = (): QualifiersContextProps => {
    const context = useContext(QualifiersContext);
    if (context === undefined) {
        throw new Error(`useQualifiers must be used within an QualifiersProvider`);
    }
    return context;
};
